import React from "react"
import { motion } from "framer-motion"
import { Target, Heart, Eye, Sparkles } from "lucide-react"
import CTASection from "../components/marketing/CTASection"
import AICapabilitiesShowcase from "../components/marketing/AICapabilitiesShowcase"

const values = [
  { icon: Target, title: "Precision", description: "Every data label, every integration — done with meticulous care and accuracy." },
  { icon: Heart, title: "Partnership", description: "We embed ourselves in your team until AI feels natural." },
  { icon: Eye, title: "Transparency", description: "You understand exactly what we do and why it works." }
]

export default function About() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span className="text-blue-300 text-sm font-medium">About Bridge</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                I'm Ryan Osmun
              </h1>

              <p className="mt-6 text-slate-300 text-lg leading-relaxed">
                Born and raised in Gilbert, Arizona, I spent 22 years working as an AutoCAD designer —
                building systems and solving technical problems for businesses of all sizes.
              </p>

              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                When AI exploded onto the scene, I saw something troubling: these powerful tools were
                built for tech giants with massive budgets and teams of engineers.
                <span className="text-white font-medium"> Local businesses were being left behind.</span>
              </p>

              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                That's why I started <span className="text-blue-400 font-semibold">Bridge</span> —
                to translate cutting-edge AI into practical solutions that small and medium businesses
                can actually use.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden border border-slate-700 bg-slate-900/50">
                <div className="aspect-square bg-gradient-to-br from-blue-500/20 via-slate-900 to-purple-500/20 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="text-8xl font-bold text-white/10">22</div>
                    <div className="text-xl text-slate-400 mt-2">Years of Technical Experience</div>
                    <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                      <div className="p-4 rounded-xl bg-slate-800/50">
                        <div className="text-3xl font-bold text-blue-400">100+</div>
                        <div className="text-sm text-slate-400">Projects Completed</div>
                      </div>
                      <div className="p-4 rounded-xl bg-slate-800/50">
                        <div className="text-3xl font-bold text-emerald-400">Local</div>
                        <div className="text-sm text-slate-400">Arizona Based</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Capabilities Showcase */}
      <AICapabilitiesShowcase />

      <section className="bg-slate-950 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white">My Core Values</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center p-8 rounded-2xl bg-slate-800/40 border border-slate-700/50"
              >
                <div className="inline-flex p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-6">
                  <val.icon className="w-7 h-7 text-blue-400" />
                </div>

                <h3 className="text-xl font-semibold text-white mb-3">
                  {val.title}
                </h3>

                <p className="text-slate-400">
                  {val.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  )
}
